<template>
  <div class="product-settings-filters">
    <ProductSettingsColorFilter
      :product-types="productTypes"
      :filters="filters"
      @update="onUpdateAppearanceGroups"
    />
    <ProductSettingsCheckboxFilter
      heading="PUBLISHING.DETAIL.PRODUCTS.TARGET_GROUP.HEADING"
      :product-types="productTypes"
      :filters="filters"
      filter-key="activeTargetGroups"
      :get-available-options="getAvailableTargetGroups"
      :get-label="getTargetGroupLabel"
      @update="onUpdateTargetGroups"
    />
    <ProductSettingsCheckboxFilter
      heading="PUBLISHING.DETAIL.PRODUCTS.ADVANCED.CATALOG.FILTERS.SIZES"
      :product-types="productTypes"
      :filters="filters"
      filter-key="activeSizeGroups"
      :get-available-options="
        compositionCategoryHelper.getAllAvailableSizeGroups
      "
      :get-label="getSizeGroupLabel"
      @update="onUpdateSizeGroups"
    />
    <ProductSettingsCheckboxFilter
      heading="PUBLISHING.DETAIL.PRODUCTS.ADVANCED.CATALOG.FILTERS.BRANDS"
      :product-types="productTypes"
      :filters="filters"
      filter-key="activeBrands"
      :get-available-options="getAvailableBrandNamesAlphabetically"
      @update="onUpdateBrands"
    />
    <ProductSettingsCheckboxFilter
      heading="PUBLISHING.DETAIL.PRODUCTS.ADVANCED.CATALOG.FILTERS.HOTSPOTS"
      :product-types="productTypes"
      :filters="filters"
      filter-key="activeHotspots"
      :get-available-options="compositionCategoryHelper.getAllAvailableHotspots"
      :get-label="getHotspotLabel"
      @update="onUpdateHotspots"
    />
    <button v-if="resetAvailable" class="text-btn" @click="resetAllFilters">
      {{ $t('FILTERS.RESET') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import compositionCategoryHelper, {
  CompositionSpecificCategoryProductType,
  TranslatedAppearanceGroup,
} from 'commons/composition/compositionCategoryHelper';
import { HotspotKey, HOTSPOTS } from '@spreadgroup/common-partner-lib/states';
import ProductSettingsColorFilter from './ProductSettingsColorFilter.vue';
import ProductSettingsCheckboxFilter from './ProductSettingsCheckboxFilter.vue';
import { ProductTypeFilters } from './productTypeFilters';
import { i18n } from 'commons/translate/i18n';
import { useStore } from 'vuex';
import { computed } from 'vue';

const { t } = i18n.global;
const store = useStore();

const { productTypes, filters } = defineProps<{
  productTypes: CompositionSpecificCategoryProductType[];
  filters: ProductTypeFilters;
}>();

const resetAvailable = computed(() => {
  return (
    filters.activeAppearanceGroups.length > 0 ||
    filters.activeBrands.length > 0 ||
    filters.activeHotspots.length > 0 ||
    filters.activeTargetGroups.length > 0 ||
    filters.activeSizeGroups.length > 0
  );
});

const emit = defineEmits<{
  updateAppearanceGroups: [groups: TranslatedAppearanceGroup[]];
  updateTargetGroups: [targetGroups: string[]];
  updateSizeGroups: [sizeGroups: string[]];
  updateBrands: [brands: string[]];
  updateHotspots: [hotspots: HotspotKey[]];
}>();

const getAvailableBrandNamesAlphabetically = (
  productTypes: CompositionSpecificCategoryProductType[],
): string[] => {
  const allBrandNames =
    compositionCategoryHelper.getAllAvailableBrandNames(productTypes);
  return allBrandNames.sort((a, b) => a.localeCompare(b));
};

const onUpdateAppearanceGroups = (groups: TranslatedAppearanceGroup[]) => {
  emit('updateAppearanceGroups', groups);
};

const onUpdateTargetGroups = (targetGroups: string[]) => {
  emit('updateTargetGroups', targetGroups);
};

const onUpdateSizeGroups = (sizeGroups: string[]) => {
  emit('updateSizeGroups', sizeGroups);
};

const onUpdateBrands = (brands: string[]) => {
  emit('updateBrands', brands);
};

const onUpdateHotspots = (hotspots: string[]) => {
  emit('updateHotspots', hotspots as HotspotKey[]);
};

const getAvailableTargetGroups = (
  productTypes: CompositionSpecificCategoryProductType[],
): string[] => {
  const allTargetGroups = store.getters['publishingCore/targetGroupLabels'] as {
    id: string;
    name: string;
  }[];

  return productTypes?.length
    ? [
        ...new Set(
          productTypes.reduce(
            (prev, current) => [
              ...prev,
              ...current.labels
                .map((label) => label.id)
                .filter((labelId) =>
                  allTargetGroups.some((tg) => tg.id === labelId),
                ),
            ],
            [] as string[],
          ),
        ),
      ]
    : [];
};

const getTargetGroupLabel = (key: string): string => {
  const allTargetGroups = store.getters['publishingCore/targetGroupLabels'] as {
    id: string;
    name: string;
  }[];
  return allTargetGroups.find((label) => label.id === key)?.name || key;
};

// TODO: Move somewhere else when publishinCore store uses typescript
type TranslatedSizeGroups = Record<string, { id: string; name: string }>;

const getSizeGroupLabel = (key: string): string => {
  const translatedSizeGroups: TranslatedSizeGroups =
    store.state.publishingCore.translations.sizeGroups;

  return translatedSizeGroups[key] ? translatedSizeGroups[key].name : key;
};

const getHotspotLabel = (key: string): string => {
  return t(HOTSPOTS[key as HotspotKey].name);
};

const resetAllFilters = () => {
  onUpdateAppearanceGroups([]);
  onUpdateBrands([]);
  onUpdateHotspots([]);
  onUpdateTargetGroups([]);
  onUpdateSizeGroups([]);
};
</script>

<style scoped lang="scss">
.product-settings-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
